import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

const buildings = [
  { icon: "ri-home-4-line", keyName: "buildingHouse" as const, keyDesc: "houseDesc" as const, keyCost: "houseCost" as const, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-400/10", border: "border-emerald-200 dark:border-emerald-400/20" },
  { icon: "ri-building-4-line", keyName: "buildingCompany" as const, keyDesc: "companyDesc" as const, keyCost: "companyCost" as const, color: "text-cyan-500", bg: "bg-cyan-50 dark:bg-cyan-400/10", border: "border-cyan-200 dark:border-cyan-400/20" },
  { icon: "ri-flashlight-line", keyName: "buildingPowerPlant" as const, keyDesc: "powerPlantDesc" as const, keyCost: "powerPlantCost" as const, color: "text-violet-500", bg: "bg-violet-50 dark:bg-violet-400/10", border: "border-violet-200 dark:border-violet-400/20" },
];

const auctionOptions = [
  { duration: "8h", icon: "ri-time-line", label: "8時間 / 8 Hours" },
  { duration: "24h", icon: "ri-24-hours-line", label: "24時間 / 24 Hours" },
  { duration: "3d", icon: "ri-calendar-line", label: "3日間 / 3 Days" },
];

export default function FeaturesSection() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { openAuthModal } = useAuth();
  const { isDark } = useTheme();

  const sectionBg1 = isDark ? "bg-[#0d0d14]" : "bg-gray-50";
  const sectionBg2 = isDark ? "bg-[#0a0a0f]" : "bg-white";
  const headingCls = isDark ? "text-white" : "text-gray-900";
  const textCls = isDark ? "text-gray-400" : "text-gray-600";
  const subtextCls = isDark ? "text-gray-500" : "text-gray-400";
  const cardBg = isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200";
  const auctionCardBg = isDark ? "bg-white/3 border-white/10 hover:border-amber-400/40" : "bg-gray-50 border-gray-200 hover:border-amber-400/60";
  const checkBg = isDark ? "bg-amber-400/10" : "bg-amber-50";

  return (
    <>
      {/* Stats Bar */}
      <section className={`border-y transition-colors duration-300 ${isDark ? "bg-[#0d0d14] border-white/5" : "bg-gray-50 border-gray-200"}`}>
        <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: "ri-earth-line", val: "8,247,000+", label: isDark ? "総販売可能区画" : "Available Blocks" },
            { icon: "ri-group-line", val: "12,483", label: isDark ? "現在の所有者数" : "Current Owners" },
            { icon: "ri-exchange-line", val: "342", label: isDark ? "本日の取引件数" : "Today's Trades" },
            { icon: "ri-auction-line", val: "56", label: isDark ? "進行中のオークション" : "Active Auctions" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${s.icon} text-amber-400 text-xl`}></i>
              </div>
              <p className={`text-2xl font-black font-['Space_Grotesk',sans-serif] ${headingCls}`}>{s.val}</p>
              <p className={`text-xs mt-1 whitespace-nowrap ${subtextCls}`}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature: Buy Land */}
      <section className={`py-24 px-6 transition-colors duration-300 ${sectionBg2}`}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 ${isDark ? "bg-amber-400/10" : "bg-amber-50"}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-amber-400 text-sm"></i>
              </div>
              <span className="text-amber-500 text-xs font-medium whitespace-nowrap">Land Ownership</span>
            </div>
            <h2 className={`text-4xl md:text-5xl font-black mb-6 font-['Space_Grotesk',sans-serif] leading-tight ${headingCls}`}>
              {t("featureLand")}
            </h2>
            <p className={`text-lg mb-8 leading-relaxed ${textCls}`}>{t("featureLandDesc")}</p>
            <div className="flex flex-col gap-3">
              {[
                "陸地のみ購入可能 (海は購入不可)",
                "1ブロック 100 coins の均一価格",
                "世界中どこでも購入できる",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className={`w-5 h-5 flex items-center justify-center rounded-full ${checkBg}`}>
                    <i className="ri-check-line text-amber-500 text-xs"></i>
                  </div>
                  <span className={`text-sm whitespace-nowrap ${textCls}`}>{item}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate("/map")}
              className={`mt-8 inline-flex items-center gap-2 border font-medium rounded-full px-6 py-3 transition-all cursor-pointer whitespace-nowrap ${isDark ? "bg-amber-400/10 hover:bg-amber-400/20 border-amber-400/30 hover:border-amber-400/60 text-amber-400" : "bg-amber-50 hover:bg-amber-100 border-amber-200 hover:border-amber-400 text-amber-600"}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-2-line text-sm"></i>
              </div>
              マップを見る
            </button>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden aspect-square">
              <img
                src="https://readdy.ai/api/search-image?query=zoomed%20in%20world%20map%20with%20golden%20glowing%20grid%20squares%20overlaid%20on%20green%20land%20masses%2C%20dark%20theme%2C%20satellite%20view%2C%20beautiful%20land%20parcels%20highlighted%20in%20amber%20color%2C%20futuristic%20map%20interface%2C%20high%20contrast%2C%20dark%20ocean%20blue%20background&width=600&height=600&seq=land1&orientation=squarish"
                alt="World map grid"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-400/10 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature: Build */}
      <section className={`py-24 px-6 transition-colors duration-300 ${sectionBg1}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-black mb-4 font-['Space_Grotesk',sans-serif] ${headingCls}`}>
              {t("featureBuildings")}
            </h2>
            <p className={`text-lg max-w-xl mx-auto ${textCls}`}>{t("featureBuildingsDesc")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {buildings.map((b) => (
              <div
                key={b.keyName}
                className={`border rounded-2xl p-6 hover:scale-105 transition-transform cursor-pointer ${isDark ? `bg-${b.bg.split("dark:")[1]?.split(" ")[0] || "white/5"} border-white/10` : `${b.bg.split(" ")[0]} ${b.border.split(" ")[0]}`}`}
                style={{ background: isDark ? "" : "" }}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 border ${isDark ? `${b.bg.split(" ")[1] || ""} ${b.border.split(" ")[1] || ""}` : `${b.bg.split(" ")[0]} ${b.border.split(" ")[0]}`}`}>
                  <i className={`${b.icon} ${b.color} text-2xl`}></i>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${headingCls}`}>{t(b.keyName)}</h3>
                <p className={`text-sm mb-4 leading-relaxed ${textCls}`}>{t(b.keyDesc)}</p>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-coin-line text-amber-400 text-sm"></i>
                  </div>
                  <span className="text-amber-500 text-sm font-semibold whitespace-nowrap">{t(b.keyCost)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature: Auction */}
      <section className={`py-24 px-6 transition-colors duration-300 ${sectionBg2}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-black mb-4 font-['Space_Grotesk',sans-serif] ${headingCls}`}>
              {t("featureAuction")}
            </h2>
            <p className={`text-lg max-w-xl mx-auto ${textCls}`}>{t("featureAuctionDesc")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {auctionOptions.map((o) => (
              <div
                key={o.duration}
                className={`border rounded-2xl p-8 text-center hover:scale-105 transition-all cursor-pointer group ${isDark ? "bg-white/3 border-white/10 hover:border-amber-400/40" : "bg-gray-50 border-gray-200 hover:border-amber-400"}`}>
                <div className={`w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-xl transition-colors ${isDark ? "bg-amber-400/10 group-hover:bg-amber-400/20" : "bg-amber-50 group-hover:bg-amber-100"}`}>
                  <i className={`${o.icon} text-amber-400 text-2xl`}></i>
                </div>
                <p className="text-amber-500 text-3xl font-black mb-2 font-['Space_Grotesk',sans-serif]">{o.duration}</p>
                <p className={`text-sm whitespace-nowrap ${textCls}`}>{o.label}</p>
                <p className={`text-xs mt-2 whitespace-nowrap ${subtextCls}`}>オークション形式</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button
              onClick={() => openAuthModal("register")}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-full px-8 py-4 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-add-line text-lg"></i>
              </div>
              今すぐ始める
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
